import gsap from 'gsap';

/**
 * Boutons du hub : ils se laissent attirer par le curseur, s'inclinent légèrement,
 * leur icône et leur titre bougent à des vitesses différentes, et un reflet suit la souris.
 * À placer sur .button-bar.
 *
 * Le JS ne fait que piloter des variables CSS (--mx, --my, --ta, --gx...) ;
 * le SCSS (hubfx.scss) les applique avec les propriétés `translate` et `rotate`,
 * qui s'ajoutent à ton `transform` de survol au lieu de le remplacer.
 */
export default class MagneticButtons {
  constructor(element) {
    this.element = element;

    this.options = {
      selector: '.hub-button',
      maxPull: 8, // déplacement max du bouton (px)
      maxTilt: 7, // inclinaison max (degrés)
    };

    // Seulement avec une vraie souris
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.items = [...this.element.querySelectorAll(this.options.selector)].map((el) =>
      this.setup(el)
    );
  }

  setup(el) {
    // Le reflet est positionné par rapport au bouton
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';

    const glow = document.createElement('span');
    glow.className = 'hubbtn-glow';
    glow.setAttribute('aria-hidden', 'true');
    el.appendChild(glow);

    const item = {
      el,
      glow,
      rect: null,
      state: { x: 0, y: 0, rx: 0, ry: 0, gx: 0, gy: 0, g: 0 },
    };

    item.render = () => this.render(item);
    item.onEnter = (e) => {
      if (e.pointerType !== 'mouse') return;
      // Mesuré avant que le bouton ne bouge : évite l'effet de rétroaction
      item.rect = el.getBoundingClientRect();
    };
    item.onMove = (e) => this.onMove(e, item);
    item.onLeave = (e) => this.onLeave(e, item);

    el.addEventListener('pointerenter', item.onEnter);
    el.addEventListener('pointermove', item.onMove);
    el.addEventListener('pointerleave', item.onLeave);

    return item;
  }

  onMove(e, item) {
    if (e.pointerType !== 'mouse') return;
    if (!item.rect) item.rect = item.el.getBoundingClientRect();

    const { rect } = item;
    const { maxPull, maxTilt } = this.options;

    // Position du curseur de -1 (bord gauche/haut) à 1 (bord droit/bas)
    const nx = gsap.utils.clamp(-1, 1, ((e.clientX - rect.left) / rect.width - 0.5) * 2);
    const ny = gsap.utils.clamp(-1, 1, ((e.clientY - rect.top) / rect.height - 0.5) * 2);

    gsap.to(item.state, {
      x: nx * maxPull,
      y: ny * maxPull,
      ry: nx * maxTilt, // rotation autour de l'axe Y
      rx: -ny * maxTilt, // rotation autour de l'axe X
      gx: e.clientX - rect.left,
      gy: e.clientY - rect.top,
      g: 1,
      duration: 0.35,
      ease: 'power3.out',
      overwrite: true,
      onUpdate: item.render,
    });
  }

  onLeave(e, item) {
    if (e.pointerType !== 'mouse') return;
    item.rect = null;

    gsap.to(item.state, {
      x: 0,
      y: 0,
      rx: 0,
      ry: 0,
      g: 0,
      duration: 0.9,
      ease: 'elastic.out(1, 0.5)',
      overwrite: true,
      onUpdate: item.render,
    });
  }

  render(item) {
    const { x, y, rx, ry, gx, gy, g } = item.state;
    const s = item.el.style;

    s.setProperty('--mx', x.toFixed(2));
    s.setProperty('--my', y.toFixed(2));
    s.setProperty('--gx', gx.toFixed(1));
    s.setProperty('--gy', gy.toFixed(1));
    s.setProperty('--g', g.toFixed(3));

    // Deux rotations (X et Y) regroupées en une seule rotation autour d'un axe,
    // pour pouvoir utiliser la propriété CSS `rotate`
    const angle = Math.hypot(rx, ry);
    if (angle < 0.001) {
      s.setProperty('--tax', '0');
      s.setProperty('--tay', '0');
      s.setProperty('--taz', '1');
      s.setProperty('--ta', '0deg');
    } else {
      s.setProperty('--tax', (rx / angle).toFixed(4));
      s.setProperty('--tay', (ry / angle).toFixed(4));
      s.setProperty('--taz', '0');
      s.setProperty('--ta', `${angle.toFixed(2)}deg`);
    }
  }

  destroy() {
    this.items?.forEach((item) => {
      item.el.removeEventListener('pointerenter', item.onEnter);
      item.el.removeEventListener('pointermove', item.onMove);
      item.el.removeEventListener('pointerleave', item.onLeave);
      gsap.killTweensOf(item.state);
      item.glow.remove();
      item.el.removeAttribute('style');
    });
  }
}
